import { useCallback } from 'react';

import { collection, onSnapshot, query, where } from 'firebase/firestore';

import { useFirestore } from '@anyx/external-firebase';
import { reportAppError } from '@anyx/shared-utils';


export const useNotificationsUnreadCount = ({
  userId,
  accountId = null,
  skip,
}: {
  userId: string;
  accountId: string | null | undefined;
  skip?: boolean;
}) => {
  const { firestore } = useFirestore();
  const getUnreadNotificationsCount = useCallback(
    (onUpdate?: (count: number) => void) => {
      if (skip || !firestore) return;
      try {
        let notificationIds: string[] = [];
        let readMap = new Map<string, boolean>();
        const targetIds = [userId, accountId, 'global'].filter(Boolean);

        const calculate = (notificationIds: string[], readMap: Map<string, boolean>) => {
          const count = notificationIds.reduce(
            (total, id) => (readMap.get(id) ? total : total + 1),
            0
          );
          onUpdate?.(count);
        };

        // Subscribe to notifications
        const unsubscribeNotifications = onSnapshot(
          query(
            collection(firestore, 'notifications'),
            where('type', 'in', ['user', 'store', 'global']),
            where('targetId', 'in', targetIds)
          ),
          (snapshot) => {
            const newNotificationIds = snapshot.docs.map((doc) => doc.id);
            notificationIds = newNotificationIds;
            calculate(newNotificationIds, readMap);
          },
          (error) => {
            console.error('[notifications] Failed to subscribe to notifications:', error);
            reportAppError(error);
          }
        );

        // Subscribe to read states
        const unsubscribeStates = onSnapshot(
          collection(firestore, `users/${userId}/notificationStates`),
          (snapshot) => {
            const newReadMap = new Map(snapshot.docs.map((doc) => [doc.id, doc.data()['read']]));
            readMap = newReadMap;
            calculate(notificationIds, newReadMap);
          },
          (error) => {
            console.error('[notifications] Failed to subscribe to read states:', error);
            reportAppError(error);
          }
        );

        return () => {
          unsubscribeNotifications();
          unsubscribeStates();
        };
      } catch (err) {
        console.error('[notifications] Failed to set up unread count listeners:', err);
        reportAppError(err);
        return;
      }
    },
    [accountId, firestore, skip, userId]
  );

  return {
    getUnreadNotificationsCount,
  };
};
